import ServiceNavbar from '../shared/ServiceNavbar';
import ServiceHero from '../shared/ServiceHero';
import ServiceProcess from '../shared/ServiceProcess';
import ServiceFAQ from '../shared/ServiceFAQ';
import ServiceCTABlock from '../shared/ServiceCTABlock';
import Footer from '../../home/components/Footer';

const processSteps = [
  { icon: 'ri-discuss-fill', step: 'Step 01', title: 'Consultation', description: 'We discuss your EV, charging needs, and existing electrical capacity to find the right solution.' },
  { icon: 'ri-dashboard-3-fill', step: 'Step 02', title: 'Panel Assessment', description: 'We inspect your electrical panel to determine if an upgrade is needed before installation.' },
  { icon: 'ri-map-2-fill', step: 'Step 03', title: 'Permits & Permits', description: 'We handle all required permits and utility notifications for a compliant, documented install.' },
  { icon: 'ri-plug-2-fill', step: 'Step 04', title: 'Wiring & Install', description: 'Dedicated 240V circuit run, charger mounted and connected professionally and cleanly.' },
  { icon: 'ri-ev-charging-fill', step: 'Step 05', title: 'Test & Activate', description: 'Charging function verified, owner walkthrough completed, and permit closed out.' },
];

const faqs = [
  { question: 'What\'s the difference between Level 1 and Level 2 EV chargers?', answer: 'Level 1 uses a standard 120V outlet and adds about 4–5 miles of range per hour — suitable for plug-in hybrids or very low daily mileage. Level 2 uses a 240V dedicated circuit and adds 25–30+ miles per hour, making it ideal for full EVs. We recommend Level 2 for most homeowners.' },
  { question: 'Do I need a panel upgrade before installing a Level 2 charger?', answer: 'Not necessarily. Many homes have enough capacity to add a 50A circuit for an EV charger. During our panel assessment, we\'ll check your current capacity and let you know upfront if an upgrade is required before installation.' },
  { question: 'What brands of EV chargers do you install?', answer: 'We install all major EVSE brands including ChargePoint, Enel X JuiceBox, Leviton, Clipper Creek, Emporia, and Tesla (with adapter). We can install a charger you provide or recommend a model based on your vehicle and needs.' },
  { question: 'Can you mount the charger in my garage or outside?', answer: 'Yes. We install both indoor garage and weatherproof outdoor-rated chargers. We\'ll discuss placement options during our consultation to find the most convenient, aesthetically clean installation location.' },
  { question: 'How long does EV charger installation take?', answer: 'Most residential EV charger installations are completed in 2–4 hours, assuming no panel upgrade is needed. If a panel upgrade is required, it typically adds a full day to the project.' },
];

const EVChargersPage = () => (
  <div className="min-h-screen bg-white">
    <ServiceNavbar />

    <ServiceHero
      bgImage="https://public.readdy.ai/ai/img_res/7df3ad8255f81fb18d8320cbfd5b3b21.jpg"
      label="EV Charger Installation — Kalamazoo"
      headline="Charge Smarter"
      headlineAccent="at Home"
      subtext="Professional Level 2 EV charger installation for every major electric vehicle. Permitted, clean, and future-ready — installed by licensed electricians who know EVs."
      trustBadges={[
        { icon: 'ri-ev-charging-fill', label: 'All EV Brands' },
        { icon: 'ri-shield-star-fill', label: 'Licensed & Insured' },
        { icon: 'ri-file-shield-2-fill', label: 'Permit-Ready' },
        { icon: 'ri-flashlight-fill', label: 'Future-Proof Wiring' },
      ]}
    />

    {/* Overview — Clean white with tech specs */}
    <section className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text left */}
          <div>
            <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-5">The Service</span>
            <h2 className="text-3xl lg:text-4xl font-black text-[#0A1628] mb-5 leading-tight">
              Stop Relying on Public Chargers.<br />Own Your Charge.
            </h2>
            <p className="text-[#4A6580] text-[15px] leading-relaxed mb-5">
              A professionally installed Level 2 home charger is one of the best upgrades an EV owner can make. Wake up every morning to a full battery — no public charger wait times, no anxiety, no compromise.
            </p>
            <p className="text-[#4A6580] text-[15px] leading-relaxed mb-8">
              DOC Electric handles everything: panel capacity evaluation, dedicated 240V circuit, charger mounting, permit application, and final inspection. We install cleanly and professionally, with wiring that looks intentional — not an afterthought.
            </p>

            {/* Level comparison */}
            <div className="rounded-xl border border-[#E0E8F0] overflow-hidden mb-6">
              <div className="grid grid-cols-3 bg-[#0A1628] text-[11px] font-bold uppercase tracking-wider text-white/60 px-4 py-3">
                <span>Feature</span>
                <span className="text-center">Level 1</span>
                <span className="text-center text-[#00D9FF]">Level 2 ★</span>
              </div>
              {[
                ['Voltage', '120V', '240V'],
                ['Range/Hour', '4–5 mi', '25–35 mi'],
                ['Full Charge', '24–40 hrs', '4–8 hrs'],
                ['Circuit Needed', 'Standard', 'Dedicated 50A'],
                ['Best For', 'PHEV owners', 'All EVs'],
              ].map(([feat, l1, l2]) => (
                <div key={feat} className="grid grid-cols-3 px-4 py-3 border-t border-[#E0E8F0] text-[13px]">
                  <span className="font-semibold text-[#0A1628]">{feat}</span>
                  <span className="text-center text-[#7A9AB8]">{l1}</span>
                  <span className="text-center font-bold text-[#0A1628]">{l2}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Image right */}
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20electric%20vehicle%20being%20charged%20in%20clean%20bright%20garage%20Level%202%20charger%20mounted%20on%20wall%20sleek%20EV%20car%20plugged%20in%20minimal%20white%20garage%20interior%20technology%20clean%20design%20future&width=700&height=600&seq=ev-overview1&orientation=portrait"
              alt="EV charger installation"
              className="w-full h-[500px] object-cover object-top rounded-2xl"
            />
            <div className="absolute -bottom-5 -left-5 bg-[#0A1628] text-white rounded-xl px-5 py-4 border border-[#1E3048] shadow-[0_8px_32px_rgba(0,0,0,0.2)]">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-ev-charging-fill text-[#00D9FF] text-base" />
                </span>
                <span className="text-white font-black text-lg">25–35 mi</span>
              </div>
              <div className="text-[#5A8AAA] text-[12px]">of range added per hour</div>
              <div className="text-[#5A8AAA] text-[12px]">with Level 2 charging</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Brand logos strip */}
    <section className="bg-[#F4F7FA] py-10 border-y border-[#E0E8F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <span className="text-[11px] font-bold uppercase tracking-[2px] text-[#8BA8C8] whitespace-nowrap">Brands We Install</span>
          <div className="flex flex-wrap items-center gap-6 lg:gap-10">
            {['Tesla', 'ChargePoint', 'Leviton', 'JuiceBox', 'Emporia', 'Clipper Creek'].map((brand) => (
              <span key={brand} className="text-[#4A6A86] text-[14px] font-bold tracking-wide">{brand}</span>
            ))}
          </div>
        </div>
      </div>
    </section>

    <ServiceProcess
      title="EV Charger Installation Process"
      subtitle="From consultation to first charge — done right and documented."
      steps={processSteps}
      theme="light"
    />

    {/* Benefits — tech tiles */}
    <section className="bg-[#0A1628] py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <span className="inline-block text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-4">Why Install at Home</span>
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">The Advantages of Home Charging</h2>
          <p className="text-[#5E7A96] text-[15px] max-w-xl mx-auto">A professional home charger is safer, faster, and more economical than any public alternative.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: 'ri-battery-charge-fill', title: 'Wake Up at 100%', desc: 'Charge overnight and start every day with a full battery — no stops, no anxiety.' },
            { icon: 'ri-money-dollar-circle-fill', title: 'Lower Cost Per Mile', desc: 'Home electricity rates are significantly cheaper than public fast chargers, especially off-peak.' },
            { icon: 'ri-shield-check-fill', title: 'Safer Than Extension Cords', desc: 'A dedicated 240V circuit eliminates fire risk from improvised Level 1 charging setups.' },
            { icon: 'ri-home-4-fill', title: 'Increases Home Value', desc: 'An installed EV charger is a valued amenity that buyers increasingly expect in new home purchases.' },
            { icon: 'ri-speed-fill', title: 'Faster Than Level 1', desc: 'Level 2 charging is up to 8x faster than a standard outlet — making overnight charging practical for any EV.' },
            { icon: 'ri-leaf-fill', title: 'Smarter Grid Usage', desc: 'Smart chargers let you schedule charging during low-rate overnight hours to minimize your energy bill.' },
          ].map((benefit) => (
            <div key={benefit.title} className="bg-[#111C2D] rounded-xl p-6 border border-[#1E3048] hover:border-[#00D9FF]/20 hover:-translate-y-1 transition-all duration-300">
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#00D9FF]/10 mb-4">
                <i className={`${benefit.icon} text-[#00D9FF] text-lg`} />
              </span>
              <h3 className="text-[15px] font-bold text-white mb-2">{benefit.title}</h3>
              <p className="text-[#4A6A86] text-[13px] leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <ServiceFAQ faqs={faqs} theme="light" />
    <ServiceCTABlock
      servicePreset="EV Charger Installation"
      headline="Get Your Charger Installed"
      subtext="Fast, professional Level 2 EV charger installation in Kalamazoo and SW Michigan. Permitted, clean, and done right."
    />
    <Footer />
  </div>
);

export default EVChargersPage;
